import { useState, useEffect, useRef } from 'react'
import { Backdrop, Box, CircularProgress, Modal } from '@mui/material'

/**
 * Modal wrapper that:
 * - Closes instantly (no fade animation)
 * - Shows a CircularProgress overlay until unmount when closed normally
 * - Accepts a `loading` prop: keeps the modal open (mask visible) but replaces
 *   content with a centered spinner while async work is in progress
 */
export const ClosingAwareModal = ({ open, children, loading, ...props }) => {
  const [isClosing, setIsClosing] = useState(false)
  const wasOpen = useRef(false)
  const loadingRef = useRef(loading)

  useEffect(() => { loadingRef.current = loading }, [loading])

  useEffect(() => {
    if (open) {
      setIsClosing(false)
      wasOpen.current = true
      return
    }
    if (wasOpen.current) {
      wasOpen.current = false
      if (!loadingRef.current) {
        setIsClosing(true)
        const timer = setTimeout(() => setIsClosing(false), 400)
        return () => clearTimeout(timer)
      }
    }
  }, [open])

  return (
    <>
      <Modal open={open} {...props}>
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
            }}
          >
            <CircularProgress />
          </Box>
        ) : children}
      </Modal>
      <Backdrop open={isClosing} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress />
      </Backdrop>
    </>
  )
}
